import { lockBodyScroll, unlockBodyScroll } from './body-scroll-lock';

const PHONE_PREFIX = '+7';
const PHONE_MASK_PLACEHOLDER = '+7 (___) ___-__-__';
const SUCCESS_AUTOCLOSE_DELAY = 5000;
const CLOSE_TRANSITION_FALLBACK_DELAY = 350;
const EMAIL_PATTERN = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;
const DEFERRED_PHONE_INPUT_TYPES = new Set(['insertFromPaste', 'insertReplacementText']);

const getPhoneDigits = (value) =>
  value.replace(/\D/g, '').replace(/^7/, '').replace(/^8/, '').slice(0, 10);

const formatPhoneValue = (value) => {
  const digits = getPhoneDigits(value);

  if (!digits.length) {
    return '';
  }

  let result = PHONE_PREFIX;

  if (digits.length > 0) {
    result += ` (${digits.slice(0, 3)}`;
  }

  if (digits.length >= 4) {
    result += `) ${digits.slice(3, 6)}`;
  }

  if (digits.length >= 7) {
    result += `-${digits.slice(6, 8)}`;
  }

  if (digits.length >= 9) {
    result += `-${digits.slice(8, 10)}`;
  }

  return result;
};

const getCaretPositionForDigitIndex = (value, digitIndex) => {
  if (!value || digitIndex <= 0) {
    return 0;
  }

  const targetDigitCount = digitIndex + 1;
  let currentDigitCount = 0;

  for (let index = 0; index < value.length; index += 1) {
    if (!/\d/.test(value[index])) {
      continue;
    }

    currentDigitCount += 1;

    if (currentDigitCount === targetDigitCount) {
      return index + 1;
    }
  }

  return value.length;
};

export const initOrderModal = () => {
  const modal = document.querySelector('#order-modal');
  if (!modal) return;

  const overlay = modal.querySelector('.modal-order__overlay');
  const dialog = modal.querySelector('.modal-order__dialog');
  const form = modal.querySelector('.modal-order__form');

  const nameInput = form?.querySelector('[name="name"]');
  const phoneInput = form?.querySelector('[name="phone"]');
  const emailInput = form?.querySelector('[name="email"]');
  const cityInput = form?.querySelector('[name="city"]');
  // PHP orderForm.php ждёт name="agree"; в шаблоне front раньше было "agreement"
  const agreementInput =
    form?.querySelector('[name="agree"]') || form?.querySelector('[name="agreement"]');
  const colorInputs = Array.from(form?.querySelectorAll('[name="color"]') || []);
  const sizeInputs = Array.from(form?.querySelectorAll('[name="size"]') || []);

  const nameField = nameInput?.closest('.modal-order__field');
  const phoneField = phoneInput?.closest('.modal-order__field');
  const emailField = emailInput?.closest('.modal-order__field');
  const cityField = cityInput?.closest('.modal-order__field');
  const agreementField = agreementInput?.closest('.modal-order__checkbox');
  const colorGroup = colorInputs[0]?.closest('.modal-order__choice-group');
  const sizeGroup = sizeInputs[0]?.closest('.modal-order__choice-group');

  const openButtons = document.querySelectorAll('[data-order-open]');
  const closeButtons = modal.querySelectorAll('[data-order-close]');

  if (!overlay || !dialog || !form) {
    return;
  }

  const formControls = Array.from(form.elements);
  const firstFocusableControl =
    nameInput ||
    phoneInput ||
    emailInput ||
    cityInput ||
    form.querySelector('input:not([type="hidden"]), select, textarea, button');

  let isOpen = false;
  let successTimerId = null;
  let focusFrameId = 0;
  let closeResetTimerId = null;
  let closeTransitionHandler = null;
  let phoneSyncFrameId = 0;
  let isSubmitting = false;

  const setInputErrorState = (field, input, isInvalid) => {
    field?.classList.toggle('modal-order__field--error', isInvalid);
    input?.setAttribute('aria-invalid', String(isInvalid));
  };

  const setChoiceErrorState = (group, inputs, isInvalid) => {
    group?.classList.toggle('modal-order__choice-group--error', isInvalid);

    inputs.forEach((input) => {
      input.setAttribute('aria-invalid', String(isInvalid));
    });
  };

  const setAgreementErrorState = (isInvalid) => {
    agreementField?.classList.toggle('modal-order__checkbox--error', isInvalid);
    agreementInput?.setAttribute('aria-invalid', String(isInvalid));
  };

  const clearErrors = () => {
    setInputErrorState(nameField, nameInput, false);
    setInputErrorState(phoneField, phoneInput, false);
    setInputErrorState(emailField, emailInput, false);
    setInputErrorState(cityField, cityInput, false);
    setChoiceErrorState(colorGroup, colorInputs, false);
    setChoiceErrorState(sizeGroup, sizeInputs, false);
    setAgreementErrorState(false);
  };

  const updatePhonePlaceholder = (shouldShowMask) => {
    if (!phoneInput) return;

    phoneInput.placeholder = shouldShowMask ? PHONE_MASK_PLACEHOLDER : 'Ваш телефон';
  };

  const resetModal = () => {
    form.reset();

    if (phoneInput) {
      phoneInput.value = '';
      updatePhonePlaceholder(false);
    }

    clearErrors();
    modal.classList.remove('modal-order--success');
  };

  const clearSuccessTimer = () => {
    if (successTimerId === null) {
      return;
    }

    clearTimeout(successTimerId);
    successTimerId = null;
  };

  const clearPhoneSyncFrame = () => {
    if (!phoneSyncFrameId) {
      return;
    }

    window.cancelAnimationFrame(phoneSyncFrameId);
    phoneSyncFrameId = 0;
  };

  const scheduleSuccessReset = () => {
    clearSuccessTimer();

    successTimerId = window.setTimeout(() => {
      successTimerId = null;

      if (!isOpen || !modal.classList.contains('modal-order--success')) {
        return;
      }

      closeModal();
    }, SUCCESS_AUTOCLOSE_DELAY);
  };

  const clearCloseResetTimer = () => {
    if (closeResetTimerId === null) {
      if (closeTransitionHandler) {
        dialog.removeEventListener('transitionend', closeTransitionHandler);
        closeTransitionHandler = null;
      }

      return;
    }

    clearTimeout(closeResetTimerId);
    closeResetTimerId = null;

    if (closeTransitionHandler) {
      dialog.removeEventListener('transitionend', closeTransitionHandler);
      closeTransitionHandler = null;
    }
  };

  const scheduleResetAfterClose = () => {
    const finalizeReset = () => {
      if (closeResetTimerId !== null) {
        clearTimeout(closeResetTimerId);
        closeResetTimerId = null;
      }

      if (closeTransitionHandler) {
        dialog.removeEventListener('transitionend', closeTransitionHandler);
        closeTransitionHandler = null;
      }

      resetModal();
    };

    const handleTransitionEnd = (event) => {
      if (event.target !== dialog || event.propertyName !== 'opacity') {
        return;
      }

      finalizeReset();
    };

    closeTransitionHandler = handleTransitionEnd;
    closeResetTimerId = window.setTimeout(finalizeReset, CLOSE_TRANSITION_FALLBACK_DELAY);
    dialog.addEventListener('transitionend', handleTransitionEnd);
  };

  const resetCaptcha = () => {
    if (typeof window.resetOrderSmartCaptcha === 'function') {
      window.resetOrderSmartCaptcha();
    }
  };

  const openModal = () => {
    if (isOpen) return;

    clearCloseResetTimer();
    clearPhoneSyncFrame();
    window.cancelAnimationFrame(focusFrameId);
    resetModal();
    resetCaptcha();

    isOpen = true;
    modal.classList.add('modal-order--open');
    modal.setAttribute('aria-hidden', 'false');
    lockBodyScroll();

    focusFrameId = window.requestAnimationFrame(() => {
      firstFocusableControl?.focus();
    });

    if (typeof window.initOrderSmartCaptcha === 'function') {
      window.initOrderSmartCaptcha();
    }
  };

  const closeModal = () => {
    if (!isOpen) return;

    isOpen = false;
    clearSuccessTimer();
    window.cancelAnimationFrame(focusFrameId);
    clearCloseResetTimer();
    clearPhoneSyncFrame();
    resetCaptcha();

    modal.classList.remove('modal-order--open');
    modal.setAttribute('aria-hidden', 'true');
    unlockBodyScroll();

    scheduleResetAfterClose();
  };

  const validateRequiredInput = (field, input) => {
    if (!input) return true;

    const isInvalid = !input.value.trim();

    setInputErrorState(field, input, isInvalid);
    return !isInvalid;
  };

  const validateEmail = () => {
    if (!emailInput) return true;

    const emailValue = emailInput.value.trim();
    const isInvalid = !emailValue || !EMAIL_PATTERN.test(emailValue) || !emailInput.validity.valid;

    setInputErrorState(emailField, emailInput, isInvalid);
    return !isInvalid;
  };

  const syncEmailInput = () => {
    if (!emailInput) return;

    const normalizedValue = emailInput.value.replace(/\s/g, '').toLowerCase();

    if (emailInput.value !== normalizedValue) {
      emailInput.value = normalizedValue;
    }

    validateEmail();
  };

  const validatePhone = () => {
    if (!phoneInput) return true;

    const digits = phoneInput.value.replace(/\D/g, '');

    if (!phoneInput.value.trim()) {
      setInputErrorState(phoneField, phoneInput, true);
      return false;
    }

    if (digits.length !== 11 || !phoneInput.value.startsWith(PHONE_PREFIX)) {
      setInputErrorState(phoneField, phoneInput, true);
      return false;
    }

    setInputErrorState(phoneField, phoneInput, false);
    return true;
  };

  const validateChoice = (group, inputs) => {
    if (!inputs.length) return true;

    const isInvalid = !inputs.some((input) => input.checked);

    setChoiceErrorState(group, inputs, isInvalid);
    return !isInvalid;
  };

  const validateAgreement = () => {
    if (!agreementInput) return true;

    if (!agreementInput.checked) {
      setAgreementErrorState(true);
      return false;
    }

    setAgreementErrorState(false);
    return true;
  };

  const setSubmittingState = (submitting) => {
    isSubmitting = submitting;
    form.classList.toggle('modal-order__form--submitting', submitting);

    formControls.forEach((control) => {
      control.disabled = submitting;
    });
  };

  openButtons.forEach((btn) => {
    btn.addEventListener('click', openModal);
  });

  closeButtons.forEach((btn) => {
    btn.addEventListener('click', closeModal);
  });

  overlay.addEventListener('click', (event) => {
    if (event.target === overlay) {
      closeModal();
    }
  });

  dialog.addEventListener('click', (event) => {
    event.stopPropagation();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && isOpen) {
      closeModal();
    }
  });

  nameInput?.addEventListener('input', () => validateRequiredInput(nameField, nameInput));
  emailInput?.addEventListener('input', syncEmailInput);
  emailInput?.addEventListener('blur', syncEmailInput);
  cityInput?.addEventListener('input', () => validateRequiredInput(cityField, cityInput));

  colorInputs.forEach((input) => {
    input.addEventListener('change', () => validateChoice(colorGroup, colorInputs));
  });

  sizeInputs.forEach((input) => {
    input.addEventListener('change', () => validateChoice(sizeGroup, sizeInputs));
  });

  const showPhoneMask = () => {
    if (phoneInput && !phoneInput.value) {
      updatePhonePlaceholder(true);
    }
  };

  const hidePhoneMask = () => {
    if (phoneInput && !phoneInput.value) {
      updatePhonePlaceholder(false);
    }
  };

  const applyPhoneValue = (preserveCaret = false) => {
    if (!phoneInput) return;

    const activeElement = document.activeElement;
    const isPhoneFocused = activeElement === phoneInput;
    const selectionStart =
      preserveCaret && isPhoneFocused && typeof phoneInput.selectionStart === 'number'
        ? phoneInput.selectionStart
        : null;
    const digitsBeforeCaret =
      selectionStart === null
        ? null
        : getPhoneDigits(phoneInput.value.slice(0, selectionStart)).length;

    phoneInput.value = formatPhoneValue(phoneInput.value);
    validatePhone();

    if (phoneInput.value) {
      updatePhonePlaceholder(true);
    } else {
      updatePhonePlaceholder(isPhoneFocused);
    }

    if (digitsBeforeCaret === null || !isPhoneFocused) {
      return;
    }

    const caretPosition = getCaretPositionForDigitIndex(phoneInput.value, digitsBeforeCaret);
    phoneInput.setSelectionRange(caretPosition, caretPosition);
  };

  const syncPhoneInput = (event) => {
    clearPhoneSyncFrame();

    const shouldDeferSync =
      event?.type === 'input' &&
      (!event.inputType || DEFERRED_PHONE_INPUT_TYPES.has(event.inputType));

    if (shouldDeferSync) {
      phoneSyncFrameId = window.requestAnimationFrame(() => {
        phoneSyncFrameId = 0;
        applyPhoneValue(false);
      });

      return;
    }

    applyPhoneValue(event?.type === 'input');
  };

  if (phoneInput) {
    phoneInput.addEventListener('focus', showPhoneMask);
    phoneInput.addEventListener('mouseenter', showPhoneMask);
    phoneInput.addEventListener('mouseleave', hidePhoneMask);
    phoneInput.addEventListener('input', syncPhoneInput);
    phoneInput.addEventListener('change', syncPhoneInput);
    phoneInput.addEventListener('blur', () => {
      syncPhoneInput();
      hidePhoneMask();
    });
  }

  agreementInput?.addEventListener('change', validateAgreement);

  form.addEventListener('submit', (event) => {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    const isValid = [
      validateChoice(colorGroup, colorInputs),
      validateChoice(sizeGroup, sizeInputs),
      validateRequiredInput(nameField, nameInput),
      validatePhone(),
      validateEmail(),
      validateRequiredInput(cityField, cityInput),
      validateAgreement(),
    ].every(Boolean);

    if (!isValid) return;

    const body = new URLSearchParams(new FormData(form));
    if (!body.get('sessid')) {
      const sessid =
        (typeof BX !== 'undefined' && typeof BX.bitrix_sessid === 'function' && BX.bitrix_sessid()) ||
        (typeof BX !== 'undefined' && BX.message && BX.message('bitrix_sessid')) ||
        '';
      if (sessid) {
        body.append('sessid', sessid);
      }
    }
    const formAction = form.getAttribute('action')?.trim();

    if (!formAction) {
      console.error('Order form action is empty');
      return;
    }

    setSubmittingState(true);

    const sendForm = (token) => {
      if (token) {
        body.set('smart-token', token);
      }

      return fetch(formAction, {
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        },
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Order request failed with status ${response.status}`);
          }

          return response.text();
        })
        .then((text) => {
          const data = JSON.parse(text);

          if (!data.success) {
            throw new Error(data.message || 'Order request rejected');
          }

          modal.classList.add('modal-order--success');
          scheduleSuccessReset();
          resetCaptcha();
        });
    };

    const captchaPromise =
      typeof window.executeOrderSmartCaptcha === 'function'
        ? (window.initOrderSmartCaptcha?.(), window.executeOrderSmartCaptcha())
        : Promise.resolve('');

    captchaPromise
      .then(sendForm)
      .catch((error) => {
        console.error(error);
        resetCaptcha();
      })
      .finally(() => {
        setSubmittingState(false);
      });
  });
};
